import { Link } from 'react-router-dom'
import Footer from '@/components/layout/Footer'
import StrategyHero from '@/components/sections/StrategyHero'
import { motion } from 'framer-motion'
import { Target, Leaf, Zap, ArrowRight, BarChart3, Factory, Magnet } from 'lucide-react'

export default function Strategy() {
  return (
    <div className="min-h-screen bg-white font-sans text-foreground selection:bg-accent selection:text-white">
      <main className="relative">
        <StrategyHero />

        {/* Harvest and Grow – dynamic visual section */}
        <section id="strategy-content" className="relative pt-20 md:pt-28 pb-24 md:pb-32 scroll-mt-24 overflow-hidden">
          {/* Dark background */}
          <div className="absolute inset-0 bg-slate-900" />

          <div className="container relative z-10 px-6">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              className="text-center mb-16 md:mb-20"
            >
              <span className="text-sm font-semibold text-white/70 uppercase tracking-[0.2em]">Two Pillars</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-white tracking-tight mt-2">
                Harvest & Grow
              </h2>
              <p className="text-white/80 text-lg max-w-2xl mx-auto mt-4">
                Our strategy balances near-term returns with long-term value creation.
              </p>
            </motion.div>

            <div className="relative max-w-6xl mx-auto">
              {/* Glow behind tiles */}
              <div className="absolute left-1/4 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[300px] rounded-full bg-slate-600/40 blur-3xl pointer-events-none" />
              <div className="absolute right-1/4 top-1/2 translate-x-1/2 -translate-y-1/2 w-[400px] h-[300px] rounded-full bg-slate-600/40 blur-3xl pointer-events-none" />
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[350px] rounded-full bg-slate-700/30 blur-3xl pointer-events-none" />
              <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
              {/* Harvest card */}
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group relative p-8 md:p-10 rounded-3xl bg-white/10 backdrop-blur-md border border-white/20 shadow-2xl shadow-black/20 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-white/20 text-white group-hover:bg-white/30 group-hover:scale-110 transition-all duration-300">
                      <Leaf className="h-7 w-7" strokeWidth={1.5} />
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-white/60 uppercase tracking-wider">Phase 1</span>
                      <h3 className="text-2xl font-bold font-heading text-white">Harvest</h3>
                    </div>
                  </div>
                  <p className="text-white/90 text-base leading-relaxed mb-6">
                    Consolidate performance, optimise and deliver returns from the Lynas 2025 capital investments.
                  </p>
                  <ul className="space-y-3">
                    {[
                      'Ramp up assets in line with customer demand',
                      'Operational efficiencies and flexibility',
                      'Enhanced government engagement',
                      'Strengthen long-term customer partnerships',
                      'Defend Japanese market share',
                      'Optimal capital allocation',
                    ].map((item, i) => (
                      <motion.li
                        key={item}
                        initial={{ opacity: 0, x: -12 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: i * 0.06 }}
                        className="flex items-start gap-3 text-white/85 text-sm leading-relaxed"
                      >
                        <span className="mt-1.5 w-2 h-2 rounded-full bg-accent shrink-0 ring-2 ring-slate-400/30" />
                        {item}
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </motion.div>

              {/* Grow card */}
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group relative p-8 md:p-10 rounded-3xl bg-white/10 backdrop-blur-md border border-white/20 shadow-2xl shadow-black/20 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-accent/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-white/20 text-white group-hover:bg-white/30 group-hover:scale-110 transition-all duration-300">
                      <Zap className="h-7 w-7" strokeWidth={1.5} />
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-white/60 uppercase tracking-wider">Phase 2</span>
                      <h3 className="text-2xl font-bold font-heading text-white">Grow</h3>
                    </div>
                  </div>
                  <p className="text-white/90 text-base leading-relaxed mb-6">
                    Capitalise on strategic opportunities, consolidate our market leading position, and deliver value growth.
                  </p>
                  <ul className="space-y-3">
                    {[
                      'Add Resource and Scale',
                      'Increase Downstream Capacity',
                      'Expand into metal and magnet supply chain',
                    ].map((item, i) => (
                      <motion.li
                        key={item}
                        initial={{ opacity: 0, x: 12 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: i * 0.08 }}
                        className="flex items-start gap-3 text-white/85 text-sm leading-relaxed font-medium"
                      >
                        <span className="mt-1.5 w-2 h-2 rounded-full bg-accent shrink-0 ring-2 ring-slate-400/30" />
                        {item}
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </div>
            </div>

          </div>
        </section>

        {/* Growth pillars – accent background */}
        <section className="relative pt-20 md:pt-28 pb-24 md:pb-32 bg-accent overflow-hidden">
          <div className="container max-w-6xl mx-auto px-6 relative z-10">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl md:text-3xl font-bold font-heading text-white tracking-tight mb-12 md:mb-16 text-center"
            >
              Growth Pillars
            </motion.h2>

            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="p-8 rounded-2xl bg-white/10 border border-white/20 backdrop-blur-sm"
              >
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/20 text-white mb-6">
                  <BarChart3 className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-bold font-heading text-white mb-3">Add Resource and Scale</h3>
                <p className="text-white/90 text-sm leading-relaxed">
                  Develop Mt Weld Carbonatite for higher-grade NdPr, expand exploration and mineral resources, and add new ionic clay feedstock sources including Malaysian resources.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="p-8 rounded-2xl bg-white/10 border border-white/20 backdrop-blur-sm"
              >
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/20 text-white mb-6">
                  <Factory className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-bold font-heading text-white mb-3">Increase Downstream Capacity</h3>
                <p className="text-white/90 text-sm leading-relaxed">
                  Expand HRE separation and broaden product range in Malaysia, add Samarium and Yttrium capabilities, expand NdPr to 12ktpa, and invest in value-added specialty manufacturing.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="p-8 rounded-2xl bg-white/10 border border-white/20 backdrop-blur-sm"
              >
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/20 text-white mb-6">
                  <Magnet className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-bold font-heading text-white mb-3">Metal and Magnet Supply Chain</h3>
                <p className="text-white/90 text-sm leading-relaxed">
                  Partner with metal and magnet manufacturers, develop NdFeB magnet capacity including the MoU with JS Link for a Kuantan facility, and pursue joint ventures or direct investments.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Funding the strategy – white section */}
        <section className="container max-w-4xl mx-auto px-6 py-16 md:py-24">

          {/* Sources and uses */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl bg-slate-50 border border-slate-200"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-accent/10 text-accent">
                <Target className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold font-heading text-primary">Funding the Strategy</h3>
            </div>
            <p className="text-slate-600 leading-relaxed mb-6">
              Lynas is raising capital to provide financial flexibility to deliver the Towards 2030 strategy. Pro forma liquidity provides balance sheet strength to capitalise on growth opportunities.
            </p>
            <div className="grid sm:grid-cols-2 gap-6 text-sm">
              <div>
                <h4 className="font-semibold text-slate-800 mb-2">Strategic uses</h4>
                <ul className="space-y-1 text-slate-600">
                  <li>Add Resource and Scale</li>
                  <li>Increase Downstream Capacity</li>
                  <li>Expand into metal and magnet supply chain</li>
                  <li>Financial flexibility and general corporate purposes</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-slate-800 mb-2">Balance sheet</h4>
                <p className="text-slate-600">
                  Pro forma liquidity provides Lynas with the flexibility to pursue an attractive pipeline of growth projects aligned with the strategy.
                </p>
              </div>
            </div>
          </motion.section>

          <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/investors"
              className="inline-flex items-center gap-2 px-8 py-4 text-base font-semibold rounded-lg bg-accent text-white hover:bg-accent/90 transition-colors"
            >
              Investor Relations
              <ArrowRight className="h-5 w-5" />
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-8 py-4 text-base font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Our Story
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}
